import { Component, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  firstValueFrom,
  lastValueFrom,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import {
  attachmentsApi,
  commercialRegistrationApi,
  usersCreatorsApi,
} from 'src/app/shared/constants/api.constants';
import { Toast } from '../../shared/enums/toast';
import { IUser } from '../../shared/models/IUser.model';
import {
  AuthService,
  HelpersService,
  HttpBaseService,
  ToastService,
  usersManageApi,
} from '@evento-ng/libs';
import { UserService } from 'src/app/user/user.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-company-info',
  templateUrl: './company-info.component.html',
  styleUrls: ['./company-info.component.scss'],
})
export class CompanyInfoComponent implements OnInit {
  user$: Observable<IUser>;
  userId: string;
  companyInfoForm: UntypedFormGroup;
  showCompanyInfoForm: boolean;
  creatorId: any;
  commercialRegisterForm: UntypedFormGroup;
  showCommercialRegisterForm: boolean;
  isEditCommercialRegister: boolean;
  commercialRegisterInfo: any;
  vatNumberForm: UntypedFormGroup;
  showVatNumberForm: boolean;
  isEditVatNumber: boolean;
  vatCertificate: string;

  regions = [
    {
      id: 1,
      name: 'المدينة المنورة',
    },
    {
      id: 2,
      name: 'القسيم',
    },
    {
      id: 3,
      name: 'جدة',
    },
    {
      id: 4,
      name: 'الرياض',
    },
  ];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dataService: HttpBaseService,
    private spinner: NgxSpinnerService,
    private toast: ToastService,
    private authService: AuthService,
    public helpers: HelpersService,
    private userService: UserService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.authService.user$.subscribe((res) => {
      this.userId = res?.id;
      this.creatorId = res?.creator?.id;
      this.getUser();
    });
    this.user$ = this.userService.selectedUser$;
  }

  getUser() {
    this.dataService
      .get(`${usersManageApi}/${this.userId}`)
      .pipe(tap((user) => this.userService.selectedUser$.next(user)))
      .subscribe((res) => {
        console.log(res);
      });
  }
  private initCompanyInfoForm(): void {
    this.companyInfoForm = this.formBuilder.group({
      companyName: [null, Validators.required],
      address: [null, Validators.required],
      companyRegion: [null, Validators.required],
      companyCity: [null, Validators.required],
      registerationNo: [
        null,
        [Validators.required, Validators.pattern('^[0-9]*$')],
      ],
      vatNo: [null, [Validators.required, Validators.pattern('^[0-9]*$')]],
    });
  }

  private initCommercialRegisterForm(): void {
    this.commercialRegisterForm = this.formBuilder.group({
      commercialRegistrationNumber: [
        null,
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern('^[0-9]*$'),
        ],
      ],
      commercialRegistrationName: [null],
      commercialRegistrationExpiryDate: [null],
    });
  }

  checkRegisterationNumber(): void {
    this.spinner.show();
    firstValueFrom(
      this.dataService
        .post(commercialRegistrationApi, {
          commercialRegistrationNumber: Number(
            this.commercialRegisterForm.value.commercialRegistrationNumber
          ),
        })
        .pipe(
          tap((res) => {
            this.spinner.hide();
            this.commercialRegisterInfo = res;
            this.commercialRegisterForm.patchValue({
              commercialRegistrationName: this.commercialRegisterInfo?.crName,
              commercialRegistrationExpiryDate:
                this.commercialRegisterInfo?.expiryDate,
            });
          })
        )
    );
  }

  saveRegisterationNumber(): void {
    this.spinner.show();
    firstValueFrom(
      this.dataService
        .put(commercialRegistrationApi, {
          commercialRegistrationNumber: Number(
            this.commercialRegisterForm.value.commercialRegistrationNumber
          ),
        })
        .pipe(
          tap((_) => {
            this.spinner.hide();
            this.authService.fetchUser();
            this.closeCommercialRegister();
            this.toast.show(
              'Success',
              this.translate.instant('Profile Updated'),
              {
                classname: Toast.success,
              }
            );
          })
        )
    );
  }

  addCommercialRegister(): void {
    this.showCommercialRegisterForm = true;
    this.isEditCommercialRegister = false;
    this.commercialRegisterForm.reset();
  }
  editCommercialRegister(): void {
    this.showCommercialRegisterForm = true;
    this.isEditCommercialRegister = true;
  }
  closeCommercialRegister(): void {
    this.showCommercialRegisterForm = false;
    this.isEditCommercialRegister = false;
    this.commercialRegisterInfo = null;
    this.commercialRegisterForm.reset();
  }

  private initVatNumberForm(): void {
    this.vatNumberForm = this.formBuilder.group({
      number: [null, [Validators.required, Validators.pattern('^[0-9]*$')]],
    });
  }

  fileSelected(base64: string): void {
    this.vatCertificate = base64;
  }

  uploadAttachment(): Observable<any> {
    return this.dataService.post(attachmentsApi, { file: this.vatCertificate });
  }

  saveVat(): void {
    this.spinner.show();
    lastValueFrom(
      this.uploadAttachment().pipe(
        switchMap((res) => {
          const body = {
            number: Number(this.vatNumberForm.value.number),
            attachment: res.id,
            creatorId: this.creatorId,
          };
          return this.dataService.put(
            `${usersCreatorsApi}/vat-attachment`,
            body
          );
        }),
        tap((_) => {
          this.spinner.hide();
          this.authService.fetchUser();
          this.closeVatNumber();
          this.toast.show(
            'Success',
            this.translate.instant('Profile Updated'),
            {
              classname: Toast.success,
            }
          );
        })
      )
    );
  }

  addVatNumber(): void {
    this.showVatNumberForm = true;
    this.isEditVatNumber = false;
    this.vatNumberForm.reset();
  }
  editVatNumber(): void {
    this.showVatNumberForm = true;
    this.isEditVatNumber = true;
  }
  closeVatNumber(): void {
    this.showVatNumberForm = false;
    this.isEditVatNumber = false;
    this.vatNumberForm.reset();
  }
}
